
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Edit, BarChart2, UserPlus, Users } from 'lucide-react';
import { StaffManagedUser, updateUserByStaff } from '@/hooks/auth/staffUserManagement';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface StaffUserTableProps {
  users: StaffManagedUser[];
  loading: boolean;
  onRefresh: () => void;
  onAssignCourse: (userId: string, userName: string) => void;
  onViewAnalytics: (userId: string, userName: string) => void;
}

const StaffUserTable: React.FC<StaffUserTableProps> = ({
  users,
  loading,
  onRefresh,
  onAssignCourse,
  onViewAnalytics
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleApprovalToggle = async (user: StaffManagedUser) => {
    setUpdatingUsers(prev => new Set(prev).add(user.id));
    
    const success = await updateUserByStaff(user.id, {
      is_approved: !user.is_approved
    });

    if (success) {
      onRefresh();
    }
    
    setUpdatingUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(user.id);
      return newSet;
    });
  };

  const getStatusBadge = (isApproved: boolean) => {
    return (
      <Badge className={isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
        {isApproved ? 'Approved' : 'Pending'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Managed Users</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Managed Users ({users.length})</span>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
            <Button onClick={onRefresh} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500 mb-4">
              There are currently no student accounts to manage. Create your first student account to get started.
            </p>
            <p className="text-sm text-gray-400">
              Staff can only manage student accounts, not other staff or admin accounts.
            </p>
          </div>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No users match your search.' : 'No users found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{getStatusBadge(user.is_approved)}</TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleApprovalToggle(user)}
                              disabled={updatingUsers.has(user.id)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              {user.is_approved ? 'Unapprove User' : 'Approve User'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onAssignCourse(user.id, user.name)}>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Assign Course
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onViewAnalytics(user.id, user.name)}>
                              <BarChart2 className="mr-2 h-4 w-4" />
                              View Analytics
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffUserTable;
